import { useCallback, useEffect, useRef, useState } from 'react';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';
import { isLegacyDataPending, migrateLegacyData, markLegacyMigrationComplete } from '../services/migrateLegacyData';
import { toLocalISOString } from '../../../utils/horaLocal';
import { useAjustes } from './useAjustes';
import { useTraspaso } from './useTraspaso';
import { useArqueoCaja } from './useArqueoCaja';
import { useProductos } from './useProductos';
import { useMovimientos } from './useMovimientos';
import { useGastosInventario } from './useGastosInventario';
import { useGanancias } from './useGanancias';
import { useFechaDelDia } from './useFechaDelDia';

const ENT_CONFIG = 'config';

export function useInventario(gastosCompartidos) {
  const [metodoCalculo, setMetodoCalculo] = useState(() => cache.get(ENT_CONFIG)?.metodo || 'PROMEDIO');
  // ===== Sub-hooks =====
  const arqueoHook = useArqueoCaja();
  const ajustesHook = useAjustes(arqueoHook.arqueos);
  const traspasoHook = useTraspaso();
  const productosHook = useProductos();
  const movimientosHook = useMovimientos(metodoCalculo);
  const gastosFallback = useGastosInventario(!gastosCompartidos);
  const gastosInvHook = gastosCompartidos || gastosFallback;
  const fechaHook = useFechaDelDia();

  const gananciasHook = useGanancias({
    movimientos: movimientosHook.movimientos,
    gastos: gastosInvHook.gastos,
    ajustes: ajustesHook.ajustes,
    productos: productosHook.productos,
    esAjusteAnulado: ajustesHook.esAjusteAnulado,
    obtenerPrecioPromedioHasta: movimientosHook.obtenerPrecioPromedioHasta,
    calcularCostoMovimiento: movimientosHook.calcularCostoMovimiento,
  });

  // ===== Migración one-time de kardex_v1 =====
  const [migrating, setMigrating] = useState(false);
  const migrationStarted = useRef(false);

  // ===== Config (método + fechaConfigurada) =====
  const [configLoading, setConfigLoading] = useState(true);
  const [metaConfig, setMetaConfig] = useState(() => {
    const cached = cache.get(ENT_CONFIG);
    return {
      metodo: cached?.metodo || null,
      fechaConfigurada: cached?.fechaConfigurada || toLocalISOString(),
    };
  });

  const fetchConfig = useCallback(async () => {
    try {
      const data = await inventarioApi.getAll(ENT_CONFIG);
      if (data && typeof data === 'object') {
        setMetodoCalculo(data.metodo || 'PROMEDIO');
        if (data.migration?.done) {
          markLegacyMigrationComplete();
          setMigrating(false);
        } else if (isLegacyDataPending()) {
          setMigrating(true);
        }
        setMetaConfig(prev => ({
          metodo: data.metodo || prev.metodo,
          fechaConfigurada: data.fechaConfigurada || prev.fechaConfigurada,
        }));
        cache.set(ENT_CONFIG, data);
      }
    } catch (e) {
      if (isOfflineError(e)) {
        const cached = cache.get(ENT_CONFIG);
        if (cached) {
          if (cached.migration?.done) {
            markLegacyMigrationComplete();
            setMigrating(false);
          } else if (isLegacyDataPending()) {
            setMigrating(true);
          }
          setMetaConfig(prev => ({
            metodo: cached.metodo || prev.metodo,
            fechaConfigurada: cached.fechaConfigurada || prev.fechaConfigurada,
          }));
        }
      }
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // ===== Ejecutar migración DESPUÉS de sincronizar con el backend =====
  const hooksLoaded =
    !productosHook.loading &&
    !movimientosHook.loading &&
    !ajustesHook.loading &&
    !arqueoHook.loading &&
    !traspasoHook.loading;

  useEffect(() => {
    if (!migrating || !isLegacyDataPending() || !hooksLoaded || migrationStarted.current) return;
    migrationStarted.current = true;
    (async () => {
      try {
        console.log('[migración] Hooks sincronizados. Enviando datos locales al backend...');
        const result = await migrateLegacyData();
        console.log('[migración] Resultado:', result);
        if (result.ok && !result.skipped) {
          await Promise.all([
            fetchConfig(),
            productosHook.refetch(),
            movimientosHook.refetch(),
            gastosInvHook.refetch(),
            arqueoHook.refetch(),
            ajustesHook.refetch(),
            traspasoHook.refetch(),
          ]);
        }
      } catch (e) {
        console.error('[migración] Error:', e.response?.status, e.response?.data || e.message);
      } finally {
        setMigrating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hooksLoaded]);

  // ===== calcularArqueoCaja wrapper =====
  const calcularArqueoCaja = () => {
    return arqueoHook.calcularArqueoCaja({
      movimientosEfectivo: movimientosHook.obtenerMovimientosEnEfectivo(),
      gastos: gastosInvHook.gastos,
      ajustesDiarios: ajustesHook.obtenerAjustesDiarios(),
    });
  };

  const seleccionarMetodo = async (metodo) => {
    setMetodoCalculo(metodo);
    const updated = { ...metaConfig, metodo };
    setMetaConfig(updated);
    cache.set(ENT_CONFIG, updated);
    try {
      await inventarioApi.patch(ENT_CONFIG, { metodo });
      cache.set(ENT_CONFIG, { ...metaConfig, metodo });
    } catch (e) {
      if (isOfflineError(e)) {
        offlineQueue.enqueue({ method: 'PATCH', entidad: ENT_CONFIG, body: { metodo } });
      }
    }
  };

  return {
    refetchTodo: async () => { await Promise.all([movimientosHook.refetch(), gastosInvHook.refetch(), ajustesHook.refetch(), traspasoHook.refetch(), arqueoHook.refetch()]); },
    // Migración
    migrating,
    // Meta config
    configLoading,
    metodo: metaConfig.metodo,
    fechaConfigurada: metaConfig.fechaConfigurada,
    seleccionarMetodo,
    // Productos
    productos: productosHook.productos,
    agregarProducto: productosHook.agregarProducto,
    agregarProductosMasivo: productosHook.agregarProductosMasivo,
    // Movimientos
    calcularCostoMovimiento: movimientosHook.calcularCostoMovimiento,
    movimientos: movimientosHook.movimientos,
    registrarMovimiento: movimientosHook.registrarMovimiento,
    editarMovimiento: movimientosHook.editarMovimiento,
    anularMovimiento: movimientosHook.anularMovimiento,
    calcularStock: movimientosHook.calcularStock,
    obtenerValorInventario: movimientosHook.obtenerValorInventario,
    obtenerPrecioPromedio: movimientosHook.obtenerPrecioPromedio,
    obtenerPrecioPromedioHasta: movimientosHook.obtenerPrecioPromedioHasta,
    setMovimientos: movimientosHook.setMovimientos,
    obtenerMovimientosEnEfectivo: movimientosHook.obtenerMovimientosEnEfectivo,
    obtenerMovimientosEnQR: movimientosHook.obtenerMovimientosEnQR,
    obtenerMovimientosPorFecha: movimientosHook.obtenerMovimientosPorFecha,
    refetchMovimientos: movimientosHook.refetch,
    // Gastos inventario
    gastos: gastosInvHook.gastos,
    obtenerGastosPorFecha: gastosInvHook.obtenerGastosPorFecha,
    // Sub-hooks: ajustes, traspasos, arqueos
    ...ajustesHook,
    ...traspasoHook,
    ...arqueoHook,
    // Ganancias
    obtenerGananciasDiarias: gananciasHook.obtenerGananciasDiarias,
    obtenerGananciasPorFecha: gananciasHook.obtenerGananciasPorFecha,
    // Arqueo de caja (wrapper)
    calcularArqueoCaja,
    // Fecha del día
    fechaDelDia: fechaHook.fechaDelDia,
    cambiarFechaDelDia: fechaHook.cambiarFechaDelDia,
    obtenerFechaDelDiaFormato: fechaHook.obtenerFechaDelDiaFormato,
  };
}